<template>
  <article
    class="ch-row"
    role="listitem"
    tabindex="0"
    @click="$emit('open-detail', call)"
    @keydown.enter="$emit('open-detail', call)"
  >
    <div class="ch-row__time">
      <v-icon icon="mdi-calendar-clock-outline" size="16" class="ch-row__time-icon" />
      <div class="ch-row__time-text">
        <span class="ch-row__date">{{ dateLabel }}</span>
        <span class="ch-row__clock">{{ timeLabel }}</span>
      </div>
    </div>

    <div class="ch-row__from">
      <span class="ch-row__field-label">From</span>
      <span class="ch-row__from-value" :title="call.from_number">{{ call.from_number }}</span>
    </div>

    <div class="ch-row__caller">
      <span class="ch-row__field-label">Caller name</span>
      <span class="ch-row__caller-value" :title="callerNameLabel">{{ callerNameLabel }}</span>
    </div>

    <div class="ch-row__direction">
      <span class="ch-row__field-label">Direction</span>
      <v-icon
        v-if="directionMeta"
        :icon="directionMeta.icon"
        size="20"
        class="ch-row__direction-icon"
        :class="directionMeta.className"
        :title="directionMeta.label"
        :aria-label="directionMeta.label"
      />
      <span v-else class="ch-row__empty">—</span>
    </div>

    <div class="ch-row__status">
      <span class="ch-row__field-label">Status</span>
      <span
        v-if="statusLabel"
        class="ch-row__status-badge"
        :class="statusClass"
        :title="statusLabel"
      >
        {{ statusLabel }}
      </span>
      <span v-else class="ch-row__empty">—</span>
    </div>

    <div class="ch-row__duration">
      <span class="ch-row__field-label">Duration</span>
      <span class="ch-row__duration-badge">{{ durationLabel }}</span>
    </div>

    <div class="ch-row__actions">
      <button
        type="button"
        class="ch-row__open"
        aria-label="View conversation intelligence"
        @click.stop="$emit('open-detail', call)"
      >
        <v-icon icon="mdi-chevron-right" size="18" />
      </button>
    </div>
  </article>
</template>

<script setup>
import { computed } from 'vue'
import {
  displayOrBlank,
  formatCallDuration,
  formatIsoDate,
  formatLabel,
  formatTimeAmPm,
} from '@/utils/format'

defineOptions({ name: 'CallHistoryRow' })

const props = defineProps({
  call: { type: Object, required: true },
})

defineEmits(['open-detail'])

const DIRECTION_META = {
  inbound: {
    icon: 'mdi-phone-incoming',
    label: 'Inbound',
    className: 'ch-row__direction-icon--inbound',
  },
  outbound: {
    icon: 'mdi-phone-outgoing',
    label: 'Outbound',
    className: 'ch-row__direction-icon--outbound',
  },
}

const dateLabel = computed(() => formatIsoDate(props.call.start_time))
const timeLabel = computed(() => formatTimeAmPm(props.call.start_time))
const durationLabel = computed(() => formatCallDuration(props.call.duration_seconds))
const callerNameLabel = computed(() => displayOrBlank(props.call.caller_name))

const directionMeta = computed(() => {
  const key = String(props.call.call_direction ?? '').trim().toLowerCase()
  return DIRECTION_META[key] || null
})

const statusLabel = computed(() => {
  const status = String(props.call.call_status ?? '').trim()
  if (!status) return ''
  return formatLabel(status)
})

const statusClass = computed(() => {
  const key = String(props.call.call_status ?? '').trim().toUpperCase()
  if (key === 'COMPLETED' || key === 'COMPLETE' || key === 'ANSWERED') {
    return 'ch-row__status-badge--success'
  }
  if (
    key === 'FAILED' ||
    key === 'ERROR' ||
    key === 'BUSY' ||
    key === 'NO_ANSWER' ||
    key === 'CANCELLED' ||
    key === 'CANCELED'
  ) {
    return 'ch-row__status-badge--danger'
  }
  if (key === 'IN_PROGRESS' || key === 'RINGING' || key === 'QUEUED') {
    return 'ch-row__status-badge--info'
  }
  return 'ch-row__status-badge--neutral'
})
</script>
